import React, { useEffect, useCallback, useRef, useState } from 'react';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import { fieldRegistry } from '../../lib/fieldRegistry';
import type { FormField } from '../../types/form';
import i18n from '../../i18n';
import LayoutField from './LayoutField';
import RepeatableField from './RepeatableField';

interface FieldCardProps {
  field: FormField;
  index: number;
  totalFields: number;
  isSelected: boolean;
  isMultiSelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const FieldCard = React.memo(function FieldCard({
  field,
  index,
  totalFields,
  isSelected,
  isMultiSelected,
  onSelect,
  onRemove,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}: FieldCardProps) {
  const fieldDef = fieldRegistry[field.type];
  const isFirst = index === 0;
  const isLast = index === totalFields - 1;
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  // Add draggable + droppable functionality for reordering
  // Disable dragging when field is multi-selected
  useEffect(() => {
    const element = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;

    // Don't set up dragging if field is multi-selected
    if (isMultiSelected) {
      return dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          return Boolean(source.data.isNew || (source.data.type === 'canvas-field' && source.data.fieldId !== field.id));
        },
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'reorder-target',
              fieldId: field.id,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data);
          setClosestEdge(edge);
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      });
    }

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () => ({
          type: 'canvas-field',
          fieldId: field.id,
          fieldType: field.type,
          isNew: false,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          // Can drop if it's a palette item or a different canvas field
          return Boolean(source.data.isNew || (source.data.type === 'canvas-field' && source.data.fieldId !== field.id));
        },
        getData: ({ input, element }) => {
          return attachClosestEdge(
            {
              type: 'reorder-target',
              fieldId: field.id,
            },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          );
        },
        onDragEnter: ({ self }) => {
          const edge = extractClosestEdge(self.data);
          setClosestEdge(edge);
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [field.id, field.type, isMultiSelected]);

  const getBorderClass = () => {
    if (isDragging) {
      return 'border-primary-500 ring-2 ring-primary-300 dark:ring-primary-700';
    }
    if (isMultiSelected) {
      return 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900 bg-blue-50/30 dark:bg-blue-900/30';
    }
    if (isSelected) {
      return 'border-primary-500 ring-2 ring-primary-100 dark:ring-primary-900';
    }
    return 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600';
  };

  return (
    <div className="relative">
      {/* Drop indicator at top */}
      {closestEdge === 'top' && <DropIndicator edge="top" gap="8px" />}

      <div
        ref={ref}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        className={`group relative bg-white dark:bg-gray-800 border-2 rounded-lg p-4 ${getBorderClass()} transition-all`}
        onClick={onSelect}
      >
        {/* Multi-select checkbox */}
        {isMultiSelected && (
          <div className={`absolute ${isRTL ? '-right-1' : '-left-1'} -top-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center z-10`}>
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {/* Drag Handle & Reorder Buttons */}
        <div className={`absolute ${isRTL ? 'right-2' : 'left-2'} top-1/2 -translate-y-1/2 flex flex-col gap-0.5`}>
          {/* Drag Handle */}
          <button
            ref={dragHandleRef}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <circle cx="4" cy="3" r="1.5" />
              <circle cx="12" cy="3" r="1.5" />
              <circle cx="4" cy="8" r="1.5" />
              <circle cx="12" cy="8" r="1.5" />
              <circle cx="4" cy="13" r="1.5" />
              <circle cx="12" cy="13" r="1.5" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            disabled={isFirst}
            className={`p-1 rounded transition-colors ${
              isFirst
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            disabled={isLast}
            className={`p-1 rounded transition-colors ${
              isLast
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Field Content */}
        <div className={isRTL ? 'mr-10' : 'ml-10'}>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded text-sm">
              {fieldDef.icon}
            </span>
            <span className="font-medium text-gray-900 dark:text-white">{field.label}</span>
            {field.required && (
              <span className="text-red-500 text-sm">*</span>
            )}
          </div>

          {/* Field Preview */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {field.type === 'text' && (
              <input
                type="text"
                placeholder={field.placeholder || 'Text input'}
                className="input pointer-events-none"
                disabled
              />
            )}
            {field.type === 'email' && (
              <input
                type="email"
                placeholder={field.placeholder || 'email@example.com'}
                className="input pointer-events-none"
                disabled
              />
            )}
            {field.type === 'number' && (
              <input
                type="number"
                placeholder={field.placeholder || '0'}
                className="input pointer-events-none"
                disabled
              />
            )}
            {field.type === 'textarea' && (
              <textarea
                placeholder={field.placeholder || 'Enter text...'}
                className="input pointer-events-none"
                rows={2}
                disabled
              />
            )}
            {field.type === 'select' && (
              <select className="input pointer-events-none" disabled>
                <option>Select an option...</option>
                {field.options?.map((opt) => (
                  <option key={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
            {field.type === 'checkbox' && (
              <div className="space-y-1">
                {field.options?.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2">
                    <input type="checkbox" disabled className="rounded" />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
            {field.type === 'radio' && (
              <div className="space-y-1">
                {field.options?.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2">
                    <input type="radio" disabled />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
            {field.type === 'date' && (
              <input type="date" className="input pointer-events-none" disabled />
            )}
            {field.type === 'datetime' && (
              <input type="datetime-local" className="input pointer-events-none" disabled />
            )}
            {field.type === 'file' && (
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                <span className="text-gray-400">Click or drag to upload</span>
              </div>
            )}
            {field.type === 'richtext' && (
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 min-h-[80px] bg-gray-50 dark:bg-gray-900">
                <span className="text-gray-400">Rich text editor</span>
              </div>
            )}
            {field.type === 'signature' && (
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center h-24">
                <span className="text-gray-400">Signature pad</span>
              </div>
            )}
            {field.type === 'repeatable' && (
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                <span className="text-gray-400">Repeatable section</span>
              </div>
            )}
            {field.type === 'section' && (
              <div className="border-l-4 border-primary-500 pl-4 py-2 bg-primary-50 dark:bg-primary-900/30 rounded-r">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">{field.label || 'Section Title'}</h3>
                {field.properties.sectionDescription && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{field.properties.sectionDescription}</p>
                )}
              </div>
            )}
            {field.type === 'divider' && (
              <hr
                className="my-2"
                style={{
                  borderStyle: field.properties.dividerStyle || 'solid',
                  borderColor: field.properties.dividerColor || '#e5e7eb'
                }}
              />
            )}
            {field.type === 'page_break' && (
              <div className="border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30 text-center">
                <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="font-medium">Page Break</span>
                </div>
                <p className="text-blue-500 dark:text-blue-400 text-sm mt-1">{field.label || 'Next Page'}</p>
              </div>
            )}
            {field.type === 'layout' && (
              <div className="border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/30">
                <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                  </svg>
                  <span className="font-medium">{field.properties.columns || 2} Column Layout</span>
                </div>
                <div className="flex gap-2" style={{ gap: field.properties.columnGap === 'small' ? '0.5rem' : field.properties.columnGap === 'large' ? '1.5rem' : '1rem' }}>
                  {Array.from({ length: field.properties.columns || 2 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 border border-purple-300 dark:border-purple-700 rounded p-2 bg-white dark:bg-gray-800 min-h-[80px] flex items-center justify-center text-purple-400 dark:text-purple-500 text-sm"
                    >
                      Column {i + 1}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {field.helpText && (
            <p className="text-xs text-gray-400 mt-2">{field.helpText}</p>
          )}
        </div>

        {/* Actions */}
        <div className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Duplicate"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Drop indicator at bottom */}
      {closestEdge === 'bottom' && <DropIndicator edge="bottom" gap="8px" />}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for optimal re-render prevention
  // Only check critical properties that affect rendering
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.field.type === nextProps.field.type &&
    prevProps.field.label === nextProps.field.label &&
    prevProps.field.required === nextProps.field.required &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isMultiSelected === nextProps.isMultiSelected &&
    prevProps.index === nextProps.index &&
    prevProps.totalFields === nextProps.totalFields
  );
});

// Bulk Action Toolbar Component
function BulkActionToolbar() {
  // Use selectors to prevent unnecessary re-renders
  const selectedFieldIds = useBuilderStore((state) => state.selectedFieldIds);
  const fieldsCount = useBuilderStore((state) => state.formSchema.fields.length);
  const clipboard = useBuilderStore((state) => state.clipboard);
  const clearSelection = useBuilderStore((state) => state.clearSelection);
  const removeSelectedFields = useBuilderStore((state) => state.removeSelectedFields);
  const duplicateSelectedFields = useBuilderStore((state) => state.duplicateSelectedFields);
  const moveSelectedFields = useBuilderStore((state) => state.moveSelectedFields);
  const copySelectedFields = useBuilderStore((state) => state.copySelectedFields);
  const pasteFields = useBuilderStore((state) => state.pasteFields);
  const selectAllFields = useBuilderStore((state) => state.selectAllFields);

  const selectedCount = selectedFieldIds.length;

  if (selectedCount === 0) return null;

  return (
    <div className="sticky top-0 z-10 mb-4 bg-blue-600 text-white rounded-lg shadow-lg p-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="font-medium">{selectedCount} field{selectedCount > 1 ? 's' : ''} selected</span>
        <button
          onClick={clearSelection}
          className="text-blue-200 hover:text-white text-sm underline"
        >
          Clear
        </button>
        <button
          onClick={selectAllFields}
          className="text-blue-200 hover:text-white text-sm underline"
          title={`Select all ${fieldsCount} fields`}
        >
          Select All
        </button>
      </div>

      <div className="flex items-center gap-2">
        {/* Move Up */}
        <button
          onClick={() => moveSelectedFields('up')}
          className="p-2 hover:bg-blue-700 rounded transition-colors"
          title="Move up"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {/* Move Down */}
        <button
          onClick={() => moveSelectedFields('down')}
          className="p-2 hover:bg-blue-700 rounded transition-colors"
          title="Move down"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className="w-px h-6 bg-blue-400 mx-1" />

        {/* Copy */}
        <button
          onClick={copySelectedFields}
          className="p-2 hover:bg-blue-700 rounded transition-colors"
          title="Copy (Ctrl+C)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>

        {/* Paste */}
        <button
          onClick={pasteFields}
          className={`p-2 rounded transition-colors ${clipboard ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'}`}
          title="Paste (Ctrl+V)"
          disabled={!clipboard}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </button>

        <div className="w-px h-6 bg-blue-400 mx-1" />

        {/* Duplicate */}
        <button
          onClick={duplicateSelectedFields}
          className="p-2 hover:bg-blue-700 rounded transition-colors"
          title="Duplicate"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
          </svg>
        </button>

        {/* Delete */}
        <button
          onClick={removeSelectedFields}
          className="p-2 hover:bg-red-600 rounded transition-colors"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function FormCanvas() {
  const { t } = useTranslation('builder');

  // Use selectors to prevent unnecessary re-renders
  const fields = useBuilderStore((state) => state.formSchema.fields);
  const selectedFieldId = useBuilderStore((state) => state.selectedFieldId);
  const selectedFieldIds = useBuilderStore((state) => state.selectedFieldIds);
  const selectField = useBuilderStore((state) => state.selectField);
  const toggleFieldSelection = useBuilderStore((state) => state.toggleFieldSelection);
  const selectFieldRange = useBuilderStore((state) => state.selectFieldRange);
  const removeField = useBuilderStore((state) => state.removeField);
  const duplicateField = useBuilderStore((state) => state.duplicateField);
  const clearSelection = useBuilderStore((state) => state.clearSelection);
  const moveFieldUp = useBuilderStore((state) => state.moveFieldUp);
  const moveFieldDown = useBuilderStore((state) => state.moveFieldDown);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);

  // Add drop target for canvas
  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        type: 'canvas',
      }),
      canDrop: ({ source }) => {
        // Can drop palette items or canvas fields being moved
        return Boolean(source.data.isNew || source.data.type === 'canvas-field');
      },
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [canvasRef]);

  // Handle click with modifier keys
  const handleFieldClick = useCallback((fieldId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (e.ctrlKey || e.metaKey) {
      toggleFieldSelection(fieldId);
    } else if (e.shiftKey) {
      selectFieldRange(fieldId);
    } else {
      selectField(fieldId);
    }
  }, [toggleFieldSelection, selectFieldRange, selectField]);

  // Keyboard shortcuts - Optimized with ref pattern to prevent listener churn
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Get latest state directly from store to avoid dependencies
      const state = useBuilderStore.getState();
      const {
        selectedFieldId,
        selectedFieldIds,
        clipboard,
        selectAllFields,
        copySelectedFields,
        pasteFields,
        removeSelectedFields,
        clearSelection,
        moveFieldUp,
        moveFieldDown,
      } = state;

      // Ctrl/Cmd+A: Select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        selectAllFields();
      }

      // Ctrl/Cmd+C: Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && (selectedFieldIds.length > 0 || selectedFieldId)) {
        e.preventDefault();
        copySelectedFields();
      }

      // Ctrl/Cmd+V: Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        e.preventDefault();
        pasteFields();
      }

      // Delete/Backspace: Remove selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedFieldIds.length > 0 || selectedFieldId)) {
        e.preventDefault();
        removeSelectedFields();
      }

      // Escape: Clear selection
      if (e.key === 'Escape') {
        clearSelection();
      }

      // Arrow keys for moving selected field
      if (selectedFieldId && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          moveFieldUp(selectedFieldId);
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          moveFieldDown(selectedFieldId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty dependencies - handler stays stable

  // Click outside to clear selection
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      clearSelection();
    }
  }, [clearSelection]);

  return (
    <div
      ref={canvasRef}
      className={`flex-1 p-6 overflow-y-auto ${
        isOver ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-100 dark:bg-gray-950'
      } transition-colors`}
      onClick={handleCanvasClick}
    >
      {fields.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Start building your form</h3>
            <p className="text-gray-500 dark:text-gray-400">Drag fields from the left panel to add them here</p>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <BulkActionToolbar />
          <div className="space-y-3">
            {fields.map((field, index) => {
              if (field.type === 'layout') {
                return (
                  <LayoutField
                    key={field.id}
                    layoutField={field}
                    isSelected={selectedFieldId === field.id && selectedFieldIds.length === 0}
                    onSelect={() => selectField(field.id)}
                    onRemove={() => removeField(field.id)}
                    onDuplicate={() => duplicateField(field.id)}
                  />
                );
              }

              if (field.type === 'repeatable') {
                return (
                  <RepeatableField
                    key={field.id}
                    repeatableField={field}
                    isSelected={selectedFieldId === field.id && selectedFieldIds.length === 0}
                    onSelect={() => selectField(field.id)}
                    onRemove={() => removeField(field.id)}
                    onDuplicate={() => duplicateField(field.id)}
                  />
                );
              }

              return (
                <FieldCard
                  key={field.id}
                  field={field}
                  index={index}
                  totalFields={fields.length}
                  isSelected={selectedFieldId === field.id && selectedFieldIds.length === 0}
                  isMultiSelected={selectedFieldIds.includes(field.id)}
                  onSelect={(e) => handleFieldClick(field.id, e)}
                  onRemove={() => removeField(field.id)}
                  onDuplicate={() => duplicateField(field.id)}
                  onMoveUp={() => moveFieldUp(field.id)}
                  onMoveDown={() => moveFieldDown(field.id)}
                />
              );
            })}
          </div>

          {/* Helper text */}
          {fields.length > 0 && selectedFieldIds.length === 0 && (
            <p className="text-center text-xs text-gray-400 mt-4">
              {t('canvas.helperText')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
